import React, { useState, useRef } from 'react';
import axios from 'axios';
import './App.css';

// ── ICONS ────────────────────────────────────────────
const IconLeaf = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#071a11" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 3c-1.2 5.4-5 8-9 9 0 5 3 9 9 9s9-4 9-9c-5 0-7.8-3-9-9z"/>
  </svg>
);

const IconActivity = ({ color = 'currentColor', size = 14 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth="2">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const IconThermometer = ({ color = 'currentColor' }) => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth="2">
    <path strokeLinecap="round" d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z"/>
  </svg>
);

const IconGauge = ({ color = 'currentColor' }) => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth="2">
    <path strokeLinecap="round" d="M12 2a10 10 0 100 20 10 10 0 000-20zM12 12l4-4"/>
    <circle cx="12" cy="12" r="1" fill={color}/>
  </svg>
);

const IconSun = ({ color = 'currentColor' }) => (
  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/>
    <line x1="12" y1="18" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="7.05" y2="7.05"/>
    <line x1="16.95" y1="16.95" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="6" y2="12"/>
    <line x1="18" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="7.05" y2="16.95"/>
    <line x1="16.95" y1="7.05" x2="19.78" y2="4.22"/>
  </svg>
);

const IconTerminal = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth="2">
    <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
  </svg>
);

const IconPlay = () => (
  <svg width="14" height="14" fill="#071a11" viewBox="0 0 24 24">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const IconCpu = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="6" height="6"/>
    <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M20 9h2M2 15h2M20 15h2"/>
    <rect x="4" y="4" width="16" height="16" rx="2"/>
  </svg>
);

// ── HELPERS ──────────────────────────────────────────
const getVibStatus = (v) => v < 80 ? ['ok','NOMINAL'] : v < 120 ? ['warn','ELEVATED'] : ['danger','CRITICAL'];
const getHeatStatus = (h) => h < 70 ? ['ok','NOMINAL'] : h < 100 ? ['warn','HOT'] : ['danger','OVERHEAT'];
const getPsiStatus = (p) => p < 90 ? ['ok','NOMINAL'] : p < 130 ? ['warn','HIGH'] : ['danger','CRITICAL'];

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
const toBarPct = (val, min, max) => `${clamp(((val - min) / (max - min)) * 100, 0, 100)}%`;

const barColor = (status) => ({
  ok: 'var(--accent)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
}[status]);

// ── SENSOR CARD ───────────────────────────────────────
const SensorCard = ({
  label,
  unit,
  value,
  onChange,
  icon,
  dotColor,
  barMin,
  barMax,
  status
}) => (
  <div className="sensor-card">
    <div className="sensor-card-header">
      <div className="sensor-label">
        <div
          className="sensor-label-dot"
          style={{ background: dotColor }}
        />
        {icon}
        {label}
      </div>

      <span className="sensor-unit">{unit}</span>
    </div>

    <input
      type="number"
      className="sensor-input"
      value={value}
      min={barMin}
      max={barMax}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue === '' ? 0 : Number(newValue));
      }}
    />

    <input
      type="range"
      className="sensor-slider"
      min={barMin}
      max={barMax}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />

    <div className="sensor-bar-wrap">
      <div
        className="sensor-bar"
        style={{
          width: toBarPct(value, barMin, barMax),
          background: barColor(status)
        }}
      />
    </div>
  </div>
);

// ── MAIN APP ──────────────────────────────────────────
function App() {
  const [data, setData] = useState({ vibration: 100, heat: 85, pressure: 110 });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [bgOpacity, setBgOpacity] = useState(0);
  const terminalRef = useRef(null);

  const [vibStatus, vibLabel] = getVibStatus(data.vibration);
  const [heatStatus, heatLabel] = getHeatStatus(data.heat);
  const [psiStatus, psiLabel] = getPsiStatus(data.pressure);

  const overallStatus = [vibStatus, heatStatus, psiStatus].includes('danger')
    ? 'danger' : [vibStatus, heatStatus, psiStatus].includes('warn')
    ? 'warn' : 'ok';

  const overallLabel = overallStatus === 'danger' ? 'FAULT DETECTED'
    : overallStatus === 'warn' ? 'ATTENTION REQ.'
    : 'ALL SYSTEMS OK';

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    setResult('');
  };

  const runAnalysis = async () => {
    setLoading(true);
    setResult('');
    try {
      const res = await axios.post('https://plant-agent-backend.onrender.com/analyze', data);
      setResult(res.data.response);
    } catch {
      setResult('Connection error: backend unreachable.\nVerify the FastAPI server is running at plant-agent-backend.onrender.com');
    }
    setLoading(false);
    setTimeout(() => terminalRef.current?.scrollTo({ top: 99999, behavior: 'smooth' }), 100);
  };

  return (
    <div className="app-shell">

      {/* ── TOPBAR ── */}
      <nav className="topbar">
        <div className="topbar-brand">
          <div className="brand-icon"><IconLeaf /></div>
          <span className="brand-name">PlantCore</span>
          <span className="brand-version">v3.3</span>
        </div>

        <div className="topbar-center">
          <button className="tab-btn active">Diagnostics</button>
          <button className="tab-btn">Telemetry</button>
          <button className="tab-btn">Logs</button>
        </div>

        <div className="topbar-right">
          <div className={`sys-pill ${loading ? 'analyzing' : 'online'}`}>
            <div className="dot-live" />
            {loading ? 'ANALYZING' : 'ONLINE'}
          </div>
        </div>
      </nav>

      {/* ── MAIN ── */}
      <div className="main-layout">

        {/* ── SIDEBAR ── */}
        <aside className="sidebar">
          <div className="sidebar-section-label">Sensor inputs</div>

          <SensorCard
            label="Vibration"
            unit="Hz"
            value={data.vibration}
            onChange={(v) => handleChange('vibration', v)}
            icon={<IconActivity color="var(--text-3)" />}
            dotColor={barColor(vibStatus)}
            barMin={0} barMax={200}
            status={vibStatus}
          />

          <SensorCard
            label="Temperature"
            unit="°C"
            value={data.heat}
            onChange={(v) => handleChange('heat', v)}
            icon={<IconThermometer color="var(--text-3)" />}
            dotColor={barColor(heatStatus)}
            barMin={0} barMax={150}
            status={heatStatus}
          />

          <SensorCard
            label="Pressure"
            unit="PSI"
            value={data.pressure}
            onChange={(v) => handleChange('pressure', v)}
            icon={<IconGauge color="var(--text-3)" />}
            dotColor={barColor(psiStatus)}
            barMin={0} barMax={200}
            status={psiStatus}
          />

          <div className="slider-card">
            <div className="slider-header">
              <div className="slider-title">
                <IconSun color="var(--text-3)" />
                Background intensity
              </div>
              <span className="slider-val">{Math.round(bgOpacity * 100)}%</span>
            </div>
            <input
              type="range" min="0" max="1" step="0.01"
              value={bgOpacity}
              onChange={(e) => setBgOpacity(Number(e.target.value))}
            />
          </div>

          <button className="btn-execute" onClick={runAnalysis} disabled={loading}>
            {loading
              ? <><div className="spinner" /> Running analysis…</>
              : <><IconPlay /> Run diagnostics</>
            }
            {!loading && <span className="btn-shimmer" />}
          </button>
        </aside>

        {/* ── CONTENT ── */}
        <div className="content-area">

          {/* ── METRIC STRIP ── */}
          <div className="metric-strip">
            <div className="metric-cell">
              <div className="metric-label">Vibration</div>
              <div className="metric-value">{data.vibration}<span style={{fontSize:'14px',color:'var(--text-3)',marginLeft:'3px'}}>Hz</span></div>
              <div className={`metric-badge badge-${vibStatus}`}>
                <div style={{width:5,height:5,borderRadius:'50%',background:'currentColor',flexShrink:0}}/>
                {vibLabel}
              </div>
            </div>

            <div className="metric-cell">
              <div className="metric-label">Temperature</div>
              <div className="metric-value">{data.heat}<span style={{fontSize:'14px',color:'var(--text-3)',marginLeft:'3px'}}>°C</span></div>
              <div className={`metric-badge badge-${heatStatus}`}>
                <div style={{width:5,height:5,borderRadius:'50%',background:'currentColor',flexShrink:0}}/>
                {heatLabel}
              </div>
            </div>

            <div className="metric-cell">
              <div className="metric-label">Pressure</div>
              <div className="metric-value">{data.pressure}<span style={{fontSize:'14px',color:'var(--text-3)',marginLeft:'3px'}}>PSI</span></div>
              <div className={`metric-badge badge-${psiStatus}`}>
                <div style={{width:5,height:5,borderRadius:'50%',background:'currentColor',flexShrink:0}}/>
                {psiLabel}
              </div>
            </div>

            <div className="metric-cell">
              <div className="metric-label">System health</div>
              <div className="metric-value" style={{fontSize:'18px',color: overallStatus === 'ok' ? 'var(--accent)' : overallStatus === 'warn' ? 'var(--warn)' : 'var(--danger)'}}>
                {overallLabel}
              </div>
              <div className={`metric-badge badge-${overallStatus}`}>
                <IconCpu />
                LangChain · IALM
              </div>
            </div>
          </div>

          {/* ── TERMINAL ── */}
          <div className="terminal-wrap">
            <div className="terminal-topbar">
              <div className="terminal-dots">
                <div className="tdot tdot-r"/>
                <div className="tdot tdot-y"/>
                <div className="tdot tdot-g"/>
              </div>
              <div className="terminal-path">
                <span style={{color:'var(--accent)'}}>root</span>
                <span className="terminal-path-sep">@</span>
                <span>plant-ai</span>
                <span className="terminal-path-sep">:</span>
                <span style={{color:'var(--blue)'}}>~/diagnostics</span>
              </div>
              <div className="terminal-actions">
                <button className="term-btn" onClick={() => setResult('')}>Clear</button>
                <button className="term-btn" onClick={runAnalysis} disabled={loading}>
                  <IconTerminal size={11} />
                </button>
              </div>
            </div>

            <div className="terminal-body" ref={terminalRef}>
              {!result && !loading ? (
                <div className="term-empty">
                  <div className="term-empty-icon">
                    <IconTerminal color="var(--text-3)" size={20} />
                  </div>
                  <div className="term-empty-label">No output yet</div>
                  <div className="term-empty-sub">Set sensor values and run diagnostics</div>
                </div>
              ) : (
                <>
                  <div className="term-prompt">
                    <span className="term-prompt-prefix">root@plant-ai:~$</span>
                    <span
  className="term-prompt-cmd"
  style={{
    wordBreak: 'break-word',
    overflowWrap: 'anywhere'
  }}
>
  ./analyze_telemetry --vib={data.vibration} --heat={data.heat} --psi={data.pressure}
</span>
                  </div>

                  {loading && (
                    <div className="term-loading">
                      <div className="spinner"/>
                      Running LangChain agent…
                    </div>
                  )}

                  {result && (
                    <div className="term-output">
                      {result}
                    </div>
                  )}

                  {!loading && <span className="cursor" />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATUS BAR ── */}
      <div className="statusbar">
        <div className="sb-item sb-ok">● online</div>
        <div className="sb-item"><span className="sb-sep">|</span></div>
        <div className="sb-item">LangChain · FastAPI</div>
        <div className="sb-item"><span className="sb-sep">|</span></div>
        <div className="sb-item">plant-agent-backend.onrender.com</div>
        <div className="sb-item sb-ml">
          vib:{data.vibration}Hz &nbsp;·&nbsp; heat:{data.heat}°C &nbsp;·&nbsp; psi:{data.pressure}
        </div>
      </div>

    </div>
  );
}

export default App;
